import { OrderListSkeleton } from '@susu/order-list'

import ActiveOrdersSkeleton from './ActiveOrdersSkeleton'
import ItemsSectionSkeleton from './ItemsSectionSkeleton'
import Skeleton from './Skeleton'

/**
 * Placeholder for the Customer Timeline card. The card itself needs the
 * customer record, so it cannot render during boot — this reserves its space
 * so the card's arrival does not push the sections below it down. It mirrors
 * `ActionCard`'s box (rounded-lg, border, 19px padding, a 20px leading icon
 * and the title/description line boxes), so the height follows from the same
 * model rather than a guessed pixel value.
 */
function TimelinePlaceholder() {
  return (
    <div className="flex flex-col max-lg:px-16" aria-busy="true">
      <div className="mb-16 flex h-28 items-center">
        <Skeleton w={120} h={20} />
      </div>
      <div className="flex w-full items-center gap-16 rounded-lg border border-border bg-surface p-[19px]">
        <Skeleton w={20} h={20} className="shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-24 items-center">
            <Skeleton h={16} className="w-[46%]" />
          </div>
          <div className="flex h-20 items-center">
            <Skeleton h={12} className="w-[68%]" />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Whether the boot screen paints placeholders for this tab. The single source
 * of truth for that question: the sections may only skip their anti-flash
 * delay on a tab this returns `true` for, since anywhere else there was no
 * placeholder for them to take over from.
 */
export function bootSkeletonCoversTab(tab: string): boolean {
  return tab === 'overview' || tab === 'orders'
}

/**
 * The opening tab's placeholders, rendered by the Hub's boot screen so the
 * shell bars and the section placeholders appear in the same frame (RP-5936).
 *
 * Without this the content column stays empty until the customer record lands,
 * and the page visibly loads in two acts — the name and menu first, the section
 * skeletons second. The sections cannot mount any earlier, because their APIs
 * are keyed on the account number that arrives with that record; this paints
 * what they are about to show.
 *
 * Only the tabs the Hub actually opens on are covered — Overview for a style
 * advisor, or for a customer with `CH-active-cards` on; Orders otherwise.
 * Deep-linking to another tab keeps the previous behaviour (an empty column
 * during boot); adding one is a matter of extending the switch below, and
 * `bootSkeletonCoversTab` above.
 */
export default function BootContentSkeleton({
  tab,
  showActiveOrders,
  showTimeline,
}: {
  /** The tab the Hub is opening on. */
  tab: string
  /** `CH-active-cards`, resolved during boot. */
  showActiveOrders: boolean
  /** Style advisor, and not a location where the timeline is hidden. */
  showTimeline: boolean
}) {
  if (tab === 'orders') {
    return (
      <div className="w-full">
        <OrderListSkeleton />
      </div>
    )
  }

  if (tab !== 'overview') {
    return null
  }

  // Mirrors the Overview tab's own containers, so the sections do not move
  // when they take over from these placeholders.
  return (
    <div className="flex flex-col gap-48 pt-24 pb-120">
      {showActiveOrders && <ActiveOrdersSkeleton />}
      {showTimeline && <TimelinePlaceholder />}
      <div className="flex flex-col gap-48">
        <ItemsSectionSkeleton testId="cart-section-skeleton" />
        <ItemsSectionSkeleton testId="browsed-section-skeleton" />
      </div>
    </div>
  )
}
